import React from 'react';
import { useSelector } from 'react-redux';

import { IState, Step } from '../../graphql/types';
import { scrollFeed, StepState } from '../utils';

import Card from './Card';
import styles from './FeedPlayer.module.css';
import StepTitleCard from './StepTitleCard';

const FeedPlayer: React.FC<{ hasChapters: boolean }> = ({ hasChapters }) => {
  const steps = useSelector<IState, Step[]>(state => state.steps);
  const openedStepIndex = useSelector<IState, number>(
    state => state.openedStepOrder ?? 0
  );

  const refs: React.RefObject<HTMLDivElement>[] = React.useMemo(
    () => steps.map(() => React.createRef<HTMLDivElement>()),
    [steps]
  );

  React.useEffect(() => {
    const index = steps.findIndex(f => f.order === openedStepIndex);

    if (index > -1) {
      if (refs[index]?.current) {
        const rect = refs[index].current?.getBoundingClientRect();

        if (rect) {
          const { top } = rect;

          if (top) {
            scrollFeed(top);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedStepIndex]);

  const stepsWithCards = React.useMemo(
    () =>
      steps.map((step: Step) => ({
        cards: step?.recording?.feedCards?.items || [],
        name: step.title,
        order: step.order,
        state: step?.recording?.transcriptResults
          ? StepState.done
          : StepState.new,
        allowRelinking:
          !step?.recording?.deleted && step?.recording?.transcriptResults,
      })),
    [steps]
  );

  const haveAnyCardsAttached = React.useMemo(() => {
    return stepsWithCards.map(o => o.cards.length > 0).filter(o => o)[0];
  }, [stepsWithCards]);

  const renderStepCards = React.useCallback(
    ({ name, cards, order, state, allowRelinking }, index: number) => {
      return (
        <div key={index} ref={refs[index]} className={styles.feedPlayerColumn}>
          {hasChapters && (
            <StepTitleCard
              title={index + 1 + '. ' + name}
              transcribeState={state}
            />
          )}
          {cards?.map(card => (
            <Card
              {...card}
              key={card.id}
              stepIndex={index}
              // graphicUrl={graphicUrls[card.id]}
              stepOrder={order}
              openedStepIndex={openedStepIndex}
              allowRelinking={allowRelinking}
            />
          ))}
        </div>
      );
    },
    [refs, openedStepIndex, hasChapters]
  );

  return (
    <>
      {haveAnyCardsAttached && stepsWithCards.length ? (
        <div id="feed-player" className={styles.content}>
          {stepsWithCards.map(renderStepCards)}
        </div>
      ) : (
        <div className={styles.contentEmptyWrapper}>
          <div className={styles.contentEmpty}>
            <h2 className={styles.transcriptionTitle}>
              You haven't added anything to your feed yet
            </h2>
            <p className={styles.transcriptionText}>
              Click on a word in your transcript on the left to attach a piece
              of content to it.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedPlayer;
